// React Dependencies
import React, { useEffect, useState } from 'react';

// External Dependencies
import moment from 'moment';
import PropTypes from 'prop-types';
import InfoIcon from '@material-ui/icons/Info';
import InnerImageZoom from 'react-inner-image-zoom';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Icon, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import 'react-inner-image-zoom/lib/InnerImageZoom/styles.min.css';

// Internal Dependencies
import apiService from '../../services.js/apiService';
import MaterialDatePicker from '../global/form/MaterialDatePicker';
import AppDatePicker from '../global/form/DatePicker';
import Loading from '../global/Loading';
import RadioBtnSelect from '../common/radioBtnSelect';
import PillButton from '../common/Button/PillButton';
import Colours from '../../styles/colours';
import MText from '../common/MText';
import MButton from '../common/MButton';
import { updateModalContent, toggleModal } from '../../actions/modal';
import { IsEmpty } from '../../common/checks';
import { Status } from '../../common/constants';
import { getReceiptAutoprocessScheduledAt } from '../../common/util';

const useStyles = makeStyles(() => ({
  thFontWeightNormal: {
    fontWeight: 'normal',
  },
}));

const ReceiptUploadDetails = (props) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [receiptUpload, setReceiptUpload] = useState(null);
  const [isUpdateDisabled, setIsUpdateDisabled] = useState(true);
  const [shallBeProcessedAt, setShallBeProcessedAt] = useState(null);

  const populateEmptyFieldsWithExtractedData = (receiptData) => {
    if (IsEmpty(receiptData) || IsEmpty(receiptData.extractedData) || receiptData.status !== Status.Pending) {
      return receiptData;
    }

    const extractedFields = ['receiptNo', 'amount', 'receiptDate', 'outlet'];
    for (const field of extractedFields) {
      if (!IsEmpty(receiptData[field])) {
        // eslint-disable-next-line no-continue
        continue;
      }
      if (
        !IsEmpty(receiptData.extractedData[field])
        && !IsEmpty(receiptData.extractedData[field].value)
      ) {
        if (field === 'receiptDate') {
          const receiptDate = moment(receiptData.extractedData[field].value).utcOffset('+08').startOf('day');
          const currentDate = moment();
          const isOlderThanThreeMonths = receiptDate.isBefore(currentDate.clone().subtract(3, 'months'), 'day');
          const isTomorrowOrFuture = receiptDate.isAfter(currentDate.startOf('day'));

          if (!isOlderThanThreeMonths && !isTomorrowOrFuture) {
            receiptData[field] = receiptDate.local(true).toISOString();
          }
        } else {
          receiptData[field] = receiptData.extractedData[field].value;
        }
      }
    }

    return receiptData;
  };

  const updateShallBeProcessedInfo = (data) => {
    if (data.status === Status.Pending && [Status.Accepted, Status.Rejected].includes(data.tempStatus)) {
      const processedAt = getReceiptAutoprocessScheduledAt(data.updatedAt);
      if (!IsEmpty(processedAt) && processedAt.isValid()) {
        setShallBeProcessedAt(`${processedAt.utcOffset('+08').format('DD MMM YYYY HH:mm')} SGT`);
      } else {
        setShallBeProcessedAt(null);
      }
    } else {
      setShallBeProcessedAt(null);
    }
  };

  const fetchReceiptUpload = async (id) => {
    try {
      setIsLoading(true);
      const data = await apiService.getReceiptUpload(id);

      setReceiptUpload(populateEmptyFieldsWithExtractedData(data));

      if (data.status === Status.Pending && (moment() >= moment(data.createdAt).add(10, 'minutes') || !IsEmpty(data.extractedData))) {
        setIsUpdateDisabled(false);
      }
      updateShallBeProcessedInfo(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const processReceiptUpload = async () => {
    try {
      setIsLoading(true);
      const receiptDate = moment(receiptUpload.receiptDate).utcOffset('+08').format('YYYY-MM-DD');
      await apiService.processReceiptUpload(receiptUpload.id, { ...receiptUpload, receiptDate });
      toast.success('Receipt Upload Updated');
      updateShallBeProcessedInfo(receiptUpload);
      setIsUpdateDisabled(true);
    } catch (error) {
      toast.error(error.message, { autoClose: 7000 });
    } finally {
      setIsLoading(false);
    }
  };

  const generateConfirmationTextProcessReceiptUpload = () => (
    <span>
      <div className="mb-4">Please confirm your processing as follow:</div>
      <table>
        <tr>
          <th width="100px" className={classes.thFontWeightNormal}>Receipt No:</th>
          <th className={classes.thFontWeightNormal}>{receiptUpload.receiptNo}</th>
        </tr>
        <tr>
          <th width="100px" className={classes.thFontWeightNormal}>Outlet:</th>
          <th className={classes.thFontWeightNormal}>{receiptUpload.outlet}</th>
        </tr>
        <tr>
          <th width="100px" className={classes.thFontWeightNormal}>Receipt Date:</th>
          <th className={classes.thFontWeightNormal}>
            {
              !IsEmpty(receiptUpload.receiptDate)
                ? moment(receiptUpload.receiptDate).utcOffset('+08').startOf('day').local(true)
                  .format('D MMM YYYY')
                : ''
            }
          </th>
        </tr>
        <tr>
          <th width="100px" className={classes.thFontWeightNormal}>Amount:</th>
          <th className={classes.thFontWeightNormal}>{receiptUpload.amount}</th>
        </tr>
        <tr>
          <th width="100px" className={classes.thFontWeightNormal} style={{ paddingTop: '1.5rem' }}>Status:</th>
          <th className={classes.thFontWeightNormal} style={{ paddingTop: '1.5rem' }}>
            {receiptUpload.status === Status.Accepted ? (
              <PillButton text="Accepted" style={{ backgroundColor: Colours.Green, cursor: 'text' }} />
            ) : (
              <PillButton text="Rejected" style={{ backgroundColor: Colours.Red, color: Colours.White, cursor: 'text' }} />
            )}
          </th>
        </tr>
        <tr>
          <th width="100px" className={classes.thFontWeightNormal} style={{ paddingTop: '.5rem' }}>Process Remarks:</th>
          <th className={classes.thFontWeightNormal} style={{ paddingTop: '.5rem' }}>{receiptUpload.processRemarks}</th>
        </tr>
      </table>
    </span>
  );

  const verifyProcessReceiptUpload = async () => {
    try {
      if (receiptUpload.status === Status.Pending) {
        toast.warn('Please select the status');
        return;
      }

      if (receiptUpload.status === Status.Accepted) {
        if (receiptUpload.outlet === 'None') {
          toast.warn('Please select the outlet');
          return;
        }
      }

      props.updateModalContent({
        title:        'Confirmation',
        content:      generateConfirmationTextProcessReceiptUpload(),
        actionButton: {
          actionText: 'Yes',
          actionType: 'primary',
          action:     () => processReceiptUpload(),
        },
        cancelText:       'Cancel',
        closeAfterAction: true,
      });

      props.toggleModal();
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    try {
      const { id } = props.match.params;

      if (IsEmpty(id)) {
        throw new Error('No Receipt Upload id found in url');
      } else {
        fetchReceiptUpload(id);
      }
    } catch (err) {
      setIsLoading(false);
      toast.error(err.message);
    }
  }, []);

  return (
    <>
      {isLoading && <Loading />}

      <div className="container-fluid" style={{ backgroundColor: 'transparent' }}>
        <div className="d-flex justify-content-start content-header">
          <h1 className="d-flex align-items-center">
            <Link to="/admin/receiptUploads" title="Back" className="d-flex align-items-center mr-3">
              <Icon className="back">arrow_back</Icon>
            </Link>
            Receipt Upload
            {' '}
            <br />
            ID:
            {' '}
            {receiptUpload ? receiptUpload.id : ''}
          </h1>
        </div>

        {!isLoading && !IsEmpty(receiptUpload) && (
          <div className="row">
            <div className="col-md-6 col-sm-12">
              <div className="col-12 mt-2 d-flex align-items-center justify-content-between">
                <MText size="1rem" text={`Shopper ID: ${!IsEmpty(receiptUpload) ? receiptUpload.shopperId : ''}`} />
                {
                  !IsEmpty(shallBeProcessedAt) && (
                    <MText size="1rem" colour={Colours.Blue} style={{ textAlign: 'right' }}>
                      {'RU will be auto '}
                      <b>{receiptUpload.tempStatus.toUpperCase()}</b>
                      {' at '}
                      {shallBeProcessedAt}
                    </MText>
                  )
                }
              </div>
              <div className="col-12 mt-2">
                <div className="d-flex align-items-center">
                  <MText size="1.25rem" className="mr-3" text="Receipt No" />
                </div>
                <TextField
                  className="mt-2"
                  value={receiptUpload.receiptNo}
                  variant="outlined"
                  multiline
                  size="small"
                  fullWidth
                  onChange={(e) => {
                    setReceiptUpload({ ...receiptUpload, receiptNo: e.target.value });
                  }}
                />
              </div>

              <div className="col-12 mt-4">
                <div className="d-flex align-items-center">
                  <MText size="1.25rem" className="mr-3" text="Outlet" />
                </div>
                <RadioBtnSelect
                  className="mt-2"
                  value={receiptUpload.outlet}
                  options={[
                    { value: 'None' },
                    { value: 'One North' },
                    { value: 'Scotts Square' },
                    { value: 'Millenia Walk' },
                  ]}
                  onChange={(outlet) => setReceiptUpload({ ...receiptUpload, outlet })}
                />
              </div>

              <div className="col-12 mt-4">
                <MText size="1.25rem" className="mr-3" text="Upload Date" />
                <MaterialDatePicker
                  readOnly
                  minDate={false}
                  value={receiptUpload.createdAt}
                  inputVariant="outlined"
                  inputStyle={{ width: '222.58px' }}
                />
              </div>

              <div className="col-12 mt-4">
                <div className="mb-2 d-flex align-items-center">
                  <MText size="1.25rem" className="mr-3" text="Receipt Date" />
                </div>
                <AppDatePicker
                  inline
                  maxDate={new Date()}
                  value={
                    !IsEmpty(receiptUpload.receiptDate)
                      ? new Date(moment(receiptUpload.receiptDate).utcOffset('+08').startOf('day').local(true))
                      : null
                  }
                  onChange={(receiptDate) => setReceiptUpload({ ...receiptUpload, receiptDate: moment(receiptDate).utcOffset('+08', true).format() })}
                />
              </div>

              <div className="col-12 mt-4">
                <div className="d-flex align-items-center">
                  <MText size="1.25rem" className="mr-3" text="Amount" />
                </div>
                <div className="d-flex">
                  <MText size="1.25rem" style={{ margin: '13px 7px 10px 0px' }} text="$" />
                  <TextField
                    className="mt-2"
                    value={receiptUpload.amount}
                    variant="outlined"
                    size="small"
                    fullWidth
                    onChange={(e) => {
                      setReceiptUpload({ ...receiptUpload, amount: e.target.value });
                    }}
                  />
                </div>
              </div>

              <div className="col-12 mt-4">
                <MText size="1.25rem" text="Status" />
                <RadioBtnSelect
                  className="mt-2"
                  value={receiptUpload.status}
                  options={[
                    { value: Status.Accepted },
                    { value: Status.Rejected },
                  ]}
                  onChange={(status) => setReceiptUpload({ ...receiptUpload, status })}
                />
              </div>

              <div className="col-12 mt-4">
                <MText size="1.25rem" text="Process Remarks" />
                <TextField
                  className="mt-2"
                  value={receiptUpload.processRemarks}
                  variant="outlined"
                  multiline
                  size="small"
                  fullWidth
                  onChange={(e) => {
                    setReceiptUpload({ ...receiptUpload, processRemarks: e.target.value });
                  }}
                />
              </div>

              <div className="col-12 mt-5 d-flex flex-column justify-content-center align-items-center">
                <MButton
                  variant="contained"
                  disabled={isUpdateDisabled}
                  colour={isUpdateDisabled ? Colours.Gray2 : Colours.Primary}
                  onClick={verifyProcessReceiptUpload}
                  textStyle={{ padding: '0 19px' }}
                  textColour={Colours.White}
                  text="PROCESS RECEIPT UPLOAD"
                />
                {isUpdateDisabled && receiptUpload.status === Status.Pending
                  && (
                    <div className="d-flex align-items-center mt-2" style={{ color: Colours.Blue }}>
                      <InfoIcon fontSize="small" color="inherit" />
                      <MText
                        size="1rem"
                        colour="inherit"
                        className="ml-1"
                        text="The record is currently under pre-processing"
                      />
                    </div>
                  )}
              </div>
            </div>

            <div className="col-md-6 col-sm-12">
              <div style={{ position: 'fixed', height: '100vh', overflowY: 'auto', padding: '20px 30px 150px' }}>
                {receiptUpload.images.map((item) => (
                  <div key={item.substring(item.lastIndexOf('/') + 1)} style={{ marginBottom: 20 }}>
                    <InnerImageZoom
                      src={item}
                      zoomSrc={item}
                      zoomType="click"
                      moveType="drag"
                      zoomScale={1}
                      width={500}
                      height={500}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

ReceiptUploadDetails.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  }).isRequired,
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  updateModalContent,
  toggleModal,
};

export default connect(null, mapDispatchToProps)(ReceiptUploadDetails);
